/**
 * @file
 * Table of Contents Navigation behavior.
 *
 * @description
 * Initialises tocbot for TOC markup generation and uses a custom heading
 * tracker to reliably manage active-state classes and aria-current sync.
 * Scrolls the active link into view below the md breakpoint.
 */

import tocbot from 'tocbot';
import { BREAKPOINTS } from '../../src/variables.ts';
import { createHeadingTracker, type HeadingTracker } from './heading-tracker.ts';

((Drupal, once) => {
  'use strict';

  const MD_BREAKPOINT = BREAKPOINTS.md;

  /**
   * Extra buffer (px) added on top of the sticky offset before the TOC
   * underline switches to a new active link. Without this, the underline
   * flips as soon as a heading's component merely touches the sticky top
   * line — it should instead wait until the component has scrolled 64px
   * past that line.
   */
  const ACTIVE_STATE_BUFFER = 64;

  /** CSS selector for headings that tocbot must skip. */
  const IGNORE_SELECTOR = '.sr-only, .views-exposed-form__filters-title, .toc-excluded, .toc-excluded *';

  /** Returns whether a heading should be included in TOC processing. */
  const isIncludedHeading = (heading: HTMLElement): boolean => {
    try {
      return !heading.matches(IGNORE_SELECTOR);
    } catch {
      return true;
    }
  };

  /** Give every matching heading a unique `id` so tocbot can link to it. */
  const ensureHeadingIds = (container: Element, selector: string): void => {
    const usedIds = new Set<string>();

    container.querySelectorAll<HTMLElement>(selector).forEach((heading) => {
      if (!isIncludedHeading(heading)) {
        return;
      }

      if (!heading.id) {
        const base = (heading.textContent || '')
          .trim()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');

        let candidate = base || 'section';
        let counter = 1;
        while (usedIds.has(candidate) || document.getElementById(candidate)) {
          candidate = `${candidate.replace(/-\d+$/, '')}-${counter++}`;
        }
        heading.id = candidate;
      }
      usedIds.add(heading.id);
    });
  };

  /**
   * Returns the total sticky offset (toolbar + header + CTA + mobile toc-nav below md).
   */
  const getOffset = (tocNav?: HTMLElement): number => {
    const header = document.querySelector<HTMLElement>('.tp-header');
    const displaceTop =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--drupal-displace-offset-top')) || 0;

    // The real-estate CTA bar is sticky directly below the header, so headings
    // must clear it too. Absent on pages without a CTA → contributes 0.
    const cta = document.querySelector<HTMLElement>('.real-estate-cta');

    let offset = (header?.offsetHeight ?? 0) + (cta?.offsetHeight ?? 0) + displaceTop + 20;

    if (tocNav && window.innerWidth < MD_BREAKPOINT) {
      const tocnavHeight = tocNav.offsetHeight;
      offset += tocnavHeight > 40 ? tocnavHeight : 43;
    }

    return offset;
  };

  /** Scrolls to a heading accounting for sticky offsets. */
  const scrollToHeading = (heading: HTMLElement, tocNav: HTMLElement, behavior: ScrollBehavior): void => {
    const top = heading.getBoundingClientRect().top + window.scrollY - getOffset(tocNav) + 1;
    window.scrollTo({ top, behavior });
  };

  /* ----------------------------------------------------------------------- */
  /*  Instance tracking                                                       */
  /* ----------------------------------------------------------------------- */

  interface TocInstance {
    listContainer: HTMLElement;
    contentRoot: HTMLElement;
    tracker: HeadingTracker;
    destroy: () => void;
    reinit: () => void;
  }

  const instanceMap = new WeakMap<HTMLElement, TocInstance>();

  /* ----------------------------------------------------------------------- */
  /*  Behavior                                                                */
  /* ----------------------------------------------------------------------- */

  /**
   * Initialises toc-nav with tocbot and heading tracker.
   */
  Drupal.behaviors.tpTocNav = {
    attach(context: Document | HTMLElement) {
      once('tp-toc-nav', '.toc-nav', context).forEach((element) => {
        const nav = element as HTMLElement;
        const contentSelector = nav.dataset.tocContentSelector || 'main';
        const headingSelector = nav.dataset.tocHeadingSelector || 'h1, h2, h3, h4';
        const listContainer = nav.querySelector<HTMLElement>('.toc-nav__list');

        if (!listContainer) return;

        let contentRoot: HTMLElement | null = null;
        try {
          contentRoot = document.querySelector<HTMLElement>(contentSelector);
        } catch {
          return;
        }
        if (!contentRoot) return;

        ensureHeadingIds(contentRoot, headingSelector);

        // Count visible, non-ignored headings.
        let headingCount = 0;
        try {
          headingCount = Array.from(contentRoot.querySelectorAll<HTMLElement>(headingSelector)).filter(
            isIncludedHeading
          ).length;
        } catch {
          return;
        }

        if (headingCount === 0) {
          nav.hidden = true;
          return;
        }

        /* ---- tocbot (markup only) -------------------------------------- */

        const initTocbotMarkup = (): void => {
          tocbot.destroy();
          // Active-state class names are set to no-op values so tocbot's
          // internal scroll handler doesn't conflict with our heading
          // tracker, which manages the real is-active-link / is-active-li.
          tocbot.init({
            tocElement: listContainer,
            contentElement: contentRoot,
            headingSelector,
            linkClass: 'toc-link',
            activeLinkClass: '_tocbot-active-link',
            listClass: 'toc-list',
            listItemClass: 'toc-list-item',
            activeListItemClass: '_tocbot-active-li',
            collapseDepth: 6,
            scrollSmooth: false,
            headingsOffset: getOffset(nav),
            orderedList: false,
            ignoreHiddenElements: true,
            ignoreSelector: IGNORE_SELECTOR,
            hasInnerContainers: true,
          });
        };

        initTocbotMarkup();

        /* ---- Heading tracker (active-state management) ----------------- */

        /** Syncs `aria-current` and scrolls the active link below md. */
        const onActiveChange = (activeLink: HTMLAnchorElement | null): void => {
          listContainer.querySelectorAll<HTMLAnchorElement>('.toc-link').forEach((link) => {
            if (link === activeLink) {
              link.setAttribute('aria-current', 'location');
            } else {
              link.removeAttribute('aria-current');
            }
          });

          // Below md, keep the active link visible inside the horizontal
          // scroll container. Uses manual scrollLeft instead of
          // scrollIntoView to avoid vertical page jumps. Skipped entirely
          // when the toc-nav is off-screen (e.g. Mercury Editor where the
          // nav is not sticky and has scrolled out of view).
          if (activeLink && window.innerWidth < MD_BREAKPOINT) {
            const scrollContainer = activeLink.closest<HTMLElement>('.toc-nav') ?? nav;
            const containerRect = scrollContainer.getBoundingClientRect();

            // Only scroll when the container is visible in the viewport.
            if (containerRect.bottom > 0 && containerRect.top < window.innerHeight) {
              const linkRect = activeLink.getBoundingClientRect();
              // Align the active link to the left edge of the scroll
              // container (rather than centering it), so the list starts
              // flush left on initial load and as the user scrolls the page.
              const targetLeft = scrollContainer.scrollLeft + linkRect.left - containerRect.left;
              scrollContainer.scrollTo({ left: targetLeft, behavior: 'auto' });
            }
          }
        };

        const tracker = createHeadingTracker({
          listContainer,
          contentRoot,
          headingSelector,
          ignoreSelector: IGNORE_SELECTOR,
          getOffset: () => getOffset(nav) + ACTIVE_STATE_BUFFER,
          onChange: onActiveChange,
        });

        /** Full reinit: rebuild tocbot markup then refresh tracker. */
        const reinit = (): void => {
          ensureHeadingIds(contentRoot, headingSelector);
          initTocbotMarkup();
          tracker.refresh();
        };

        /* ---- Click handler: smooth-scroll to heading ------------------- */

        const handleClick = (e: Event): void => {
          const link = (e.target as HTMLElement)?.closest<HTMLAnchorElement>('.toc-link');
          if (!link) return;

          const href = link.getAttribute('href');
          if (!href?.startsWith('#')) return;

          const heading = document.getElementById(href.slice(1));
          if (!heading) return;

          e.preventDefault();
          e.stopPropagation();

          scrollToHeading(heading, nav, 'smooth');
          history.replaceState(null, '', href);

          // Focus the heading for keyboard users.
          setTimeout(() => {
            if (!heading.getAttribute('tabindex')) {
              heading.setAttribute('tabindex', '-1');
            }
            heading.focus({ preventScroll: true });
          }, 400);
        };

        listContainer.addEventListener('click', handleClick);

        /* ---- Space key activation for a11y ----------------------------- */

        const handleKeydown = (event: KeyboardEvent): void => {
          if (event.key !== ' ') return;
          const target = event.target as HTMLElement;
          if (target.classList.contains('toc-link')) {
            event.preventDefault();
            target.click();
          }
        };

        listContainer.addEventListener('keydown', handleKeydown);

        /* ---- Log when toc-nav is at the top ---------------------------- */

        const logWhenAtTop = (): void => {
          const isPastNav = window.scrollY >= nav.offsetTop;
          document.body.classList.toggle('toc-nav-is-sticky', isPastNav);
        };

        window.addEventListener('scroll', logWhenAtTop, { passive: true });

        /* ---- Resize & displacement: reinit with fresh offset ------------ */

        let reinitTimer: ReturnType<typeof setTimeout> | undefined;

        const debouncedReinit = () => {
          clearTimeout(reinitTimer);
          reinitTimer = setTimeout(reinit, 200);
        };

        window.addEventListener('resize', debouncedReinit);
        document.addEventListener('drupalViewportOffsetChange', debouncedReinit);

        /* ---- MutationObserver: reinit when headings change ------------- */
        // Watches for headings being added, removed, or toggled via the
        // `hidden` attribute anywhere inside the content root. This keeps
        // the TOC in sync when client-side JS shows/hides sections.

        const parsedSelectors = headingSelector.split(',').map((s) => s.trim().toLowerCase());

        /** Returns true when an element (or one of its descendants) is a tracked heading. */
        const containsHeading = (node: Node): boolean => {
          if (!(node instanceof HTMLElement)) return false;
          const tag = node.tagName.toLowerCase();
          if (parsedSelectors.includes(tag)) return true;
          return parsedSelectors.some((sel) => node.querySelector(sel) !== null);
        };

        const mutationObserver = new MutationObserver((mutations) => {
          const isRelevant = mutations.some((m) => {
            // A heading (or container of headings) was added/removed.
            if (m.type === 'childList') {
              return Array.from(m.addedNodes).some(containsHeading) || Array.from(m.removedNodes).some(containsHeading);
            }
            // The `hidden` attribute changed on an element containing a heading.
            if (m.type === 'attributes' && m.attributeName === 'hidden') {
              return containsHeading(m.target);
            }
            return false;
          });

          if (isRelevant) {
            debouncedReinit();
          }
        });

        mutationObserver.observe(contentRoot, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['hidden'],
        });

        /* ---- Store instance for AJAX reinit & detach ------------------- */

        const destroy = (): void => {
          clearTimeout(reinitTimer);
          mutationObserver.disconnect();
          tracker.destroy();
          window.removeEventListener('scroll', logWhenAtTop);
          document.body.classList.remove('toc-nav-is-sticky');
          window.removeEventListener('resize', debouncedReinit);
          document.removeEventListener('drupalViewportOffsetChange', debouncedReinit);
          listContainer.removeEventListener('click', handleClick);
          listContainer.removeEventListener('keydown', handleKeydown);
          tocbot.destroy();
        };

        instanceMap.set(nav, {
          listContainer,
          contentRoot,
          tracker,
          destroy,
          reinit,
        });

        /* ---- Restore hash on load -------------------------------------- */

        if (window.location.hash) {
          const hashTarget = document.getElementById(window.location.hash.slice(1));

          if (hashTarget) {
            setTimeout(() => {
              scrollToHeading(hashTarget, nav, 'smooth');

              // Second pass corrects after sticky layout settles below md.
              setTimeout(() => scrollToHeading(hashTarget, nav, 'auto'), 450);

              if (!hashTarget.getAttribute('tabindex')) {
                hashTarget.setAttribute('tabindex', '-1');
              }
              hashTarget.focus({ preventScroll: true });
            }, 100);
          }
        }
      });

      // Re-initialise when AJAX replaces content within the content root.
      if (context !== document && context instanceof HTMLElement) {
        once.find('tp-toc-nav').forEach((el) => {
          const inst = instanceMap.get(el as HTMLElement);
          if (!inst) return;
          if (inst.contentRoot.contains(context) || inst.contentRoot === context) {
            inst.reinit();
          }
        });
      }
    },

    detach(context: Document | HTMLElement, _settings: DrupalSettings, trigger: 'unload' | 'move' | 'serialize') {
      if (trigger !== 'unload') return;

      once.find('tp-toc-nav', context).forEach((element) => {
        const inst = instanceMap.get(element as HTMLElement);
        if (inst) {
          inst.destroy();
          instanceMap.delete(element as HTMLElement);
        }
      });

      once.remove('tp-toc-nav', '.toc-nav', context);
    },
  };
})(Drupal, once);
